/**
 * Draw-call surgery (ART-BIBLE §9).
 *
 * A cabinet is authored as ~40 little meshes because that is how you *design* an object.
 * It must ship as a handful, because every mesh costs two draws once it is inked.
 * bakeStatic() merges every static mesh that shares a material into one geometry, then
 * inks the merged result. Anything marked userData.dynamic (things that move) is left alone.
 */

import { Mesh, Group, BufferGeometry, Matrix4, type Object3D, type Material } from 'three';
import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { inkOutline, computeHullNormals } from '../art/outline';

const KEEP = ['position', 'normal', 'uv'];

function stripped(geo: BufferGeometry, m: Matrix4): BufferGeometry {
  const g = new BufferGeometry();
  for (const name of KEEP) {
    const a = geo.getAttribute(name);
    if (!a) return geo.clone().applyMatrix4(m);   // incompatible; bail out honestly
    g.setAttribute(name, a.clone());
  }
  // RoundedBoxGeometry is non-indexed, most primitives are indexed. mergeGeometries
  // refuses a mixed batch, so everything is normalised to non-indexed here.
  if (geo.index) {
    g.setIndex(geo.index.clone());
    const flat = g.toNonIndexed();
    flat.applyMatrix4(m);
    return flat;
  }
  g.applyMatrix4(m);
  return g;
}

export interface BakeResult {
  merged: number;
  removed: number;
  draws: number;
}

/**
 * Merge static meshes by material. Returns counts so the showroom can show its own bill.
 * Meshes opt out with userData.dynamic (moving parts) or by not sharing a cached material.
 */
export function bakeStatic(root: Group, defaultInk = 2.4): BakeResult {
  root.updateMatrixWorld(true);
  const rootInv = new Matrix4().copy(root.matrixWorld).invert();

  const buckets = new Map<string, { mat: Material; geos: BufferGeometry[]; ink: number; warm: boolean; cast: boolean; recv: boolean }>();
  const doomed: Mesh[] = [];

  root.traverse((o: Object3D) => {
    const m = o as Mesh;
    if (!m.isMesh) return;
    if ((m as unknown as { isInstancedMesh?: boolean }).isInstancedMesh) return;
    if (m.userData.dynamic || m.userData.sphInk || m.userData.baked) return;
    const mat = m.material as Material;
    if (Array.isArray(m.material)) return;
    // only merge materials we know are shared recipes — unique-texture materials would
    // gain nothing and merging them would silently drop their texture
    if (!mat.userData?.shared) return;
    let dynamicAncestor = false;
    for (let p: Object3D | null = m.parent; p && p !== root; p = p.parent) {
      if (p.userData.dynamic) { dynamicAncestor = true; break; }
    }
    if (dynamicAncestor) return;

    const key = mat.uuid;
    let b = buckets.get(key);
    if (!b) {
      b = { mat, geos: [], ink: typeof m.userData.ink === 'number' ? m.userData.ink : defaultInk, warm: !!m.userData.warmInk, cast: false, recv: false };
      buckets.set(key, b);
    }
    const local = new Matrix4().multiplyMatrices(rootInv, m.matrixWorld);
    b.geos.push(stripped(m.geometry, local));
    b.cast ||= m.castShadow;
    b.recv ||= m.receiveShadow;
    if (m.userData.noInk) b.ink = 0;
    doomed.push(m);
  });

  let merged = 0, draws = 0;
  for (const [, b] of buckets) {
    if (b.geos.length === 0) continue;
    const geo = b.geos.length === 1 ? b.geos[0] : BufferGeometryUtils.mergeGeometries(b.geos, false);
    if (!geo) continue;
    computeHullNormals(geo);
    const mesh = new Mesh(geo, b.mat);
    mesh.castShadow = b.cast;
    mesh.receiveShadow = b.recv;
    mesh.userData.baked = true;
    mesh.userData.inked = true;
    root.add(mesh);
    draws++;
    if (b.ink > 0) {
      inkOutline(mesh, { widthPx: b.ink, body: (b.mat as { color?: { getHex(): number } }).color?.getHex(), warm: b.warm });
      draws++;
    }
    merged++;
  }

  for (const m of doomed) {
    m.removeFromParent();
    m.geometry.dispose();
  }

  return { merged, removed: doomed.length, draws };
}
